import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Skeleton,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  AutoAwesome,
  CheckCircle,
  Cancel as CancelIcon,
  ContentPaste,
} from "@mui/icons-material";
import { useAllPrices, useTherapiesAdmin, useUsageCheck } from "../../../stores/MasterTables/therapies.queries";
import { useDeletePrice, useProcessAiPriceText } from "../../../stores/MasterTables/therapies.mutations";
import { confirmDeleteWithUsage } from "./confirmDelete";
import ModalPrice from "./ModalPrice";
import type { PriceRange, ProposedPriceChange } from "./types";
import type { UsePendingChangesReturn } from "./usePendingChanges";
import type { TherapyType } from "../../../api-client";

interface PricesTabProps {
  pending: UsePendingChangesReturn;
  therapyType?: TherapyType;
}

const PricesTab: React.FC<PricesTabProps> = ({ pending, therapyType }) => {
  const { data: prices = [], isLoading } = useAllPrices() as { data: PriceRange[] | undefined; isLoading: boolean };
  const { data: therapies = [] } = useTherapiesAdmin(therapyType);
  const deletePrice = useDeletePrice();
  const checkUsage = useUsageCheck();
  const processAi = useProcessAiPriceText();

  const [search, setSearch] = useState("");
  const [therapyFilter, setTherapyFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<PriceRange | null>(null);

  // AI Processing state
  const [aiText, setAiText] = useState("");
  const [aiResults, setAiResults] = useState<ProposedPriceChange[]>([]);
  const [showAi, setShowAi] = useState(false);

  const filtered = useMemo(() => {
    let result = prices;
    if (therapyFilter) {
      result = result.filter((p: PriceRange) => p.therapy?.id === therapyFilter);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(
        (p: PriceRange) =>
          p.therapy?.id?.toLowerCase().includes(s) ||
          p.therapy?.abbreviation?.toLowerCase().includes(s)
      );
    }
    return result;
  }, [prices, search, therapyFilter]);

  const handleCreate = () => {
    setEditItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item: PriceRange) => {
    setEditItem(item);
    setModalOpen(true);
  };

  const handleDelete = async (item: PriceRange) => {
    const usage = await checkUsage("price", item.id);
    if (!confirmDeleteWithUsage(`Preis für ${item.therapy?.id}`, usage)) return;
    deletePrice.mutate(item.id);
  };

  const handleAiProcess = async () => {
    if (!aiText.trim()) return;
    try {
      const result = await processAi.mutateAsync({ rawText: aiText });
      setAiResults(result.proposedChanges || []);
    } catch {
      // handled by mutation
    }
  };

  const handleAcceptAiChange = (change: ProposedPriceChange) => {
    pending.addChange({
      key: `ai_price_${change.therapyId}_${Date.now()}`,
      entityType: "price",
      action: "update",
      data: {
        therapyId: change.therapyId,
        price: change.newPrice ?? change.price,
      },
      label: `Preis ${change.therapyId}: ${change.oldPrice}→${change.newPrice ?? change.price}`,
    });
    setAiResults((prev) => prev.filter((c) => c !== change));
  };

  const formatDate = (d: string | undefined) => {
    if (!d) return "–";
    try {
      return new Date(d).toLocaleDateString("de-DE");
    } catch {
      return d;
    }
  };

  const formatCurrency = (v: number | undefined) => {
    if (v === undefined || v === null) return "–";
    return `${v.toFixed(2)} €`;
  };

  if (isLoading) {
    return (
      <Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* AI Processing Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderColor: showAi ? "primary.main" : "divider",
          borderRadius: 2,
          transition: "all 0.2s",
        }}
      >
        <CardContent sx={{ py: showAi ? 2 : 1, "&:last-child": { pb: showAi ? 2 : 1 } }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
            onClick={() => setShowAi(!showAi)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesome sx={{ color: "primary.main" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                KI-Preisverarbeitung
              </Typography>
              <Chip label="Beta" size="small" color="info" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {showAi ? "Zuklappen" : "Aufklappen"}
            </Typography>
          </Box>

          {showAi && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Fügen Sie den kopierten Text aus der Preisliste ein. Die KI analysiert den Text und
                schlägt Preisänderungen vor.
              </Typography>

              <TextField
                multiline
                rows={6}
                fullWidth
                placeholder="Text aus der Preisliste hier einfügen…"
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": { fontFamily: "monospace", fontSize: "0.85rem" },
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button
                  variant="contained"
                  startIcon={
                    processAi.isPending ? <CircularProgress size={16} /> : <ContentPaste />
                  }
                  onClick={handleAiProcess}
                  disabled={processAi.isPending || !aiText.trim()}
                  size="small"
                >
                  {processAi.isPending ? "Verarbeitet…" : "Text verarbeiten"}
                </Button>
                {aiText && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setAiText("");
                      setAiResults([]);
                    }}
                  >
                    Zurücksetzen
                  </Button>
                )}
              </Box>

              {/* AI Results */}
              {aiResults.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Vorgeschlagene Änderungen ({aiResults.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell sx={{ fontWeight: 700, color: "#000" }}>Heilmittel</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#000" }} align="right">Alter Preis</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#000" }} align="right">Neuer Preis</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#000" }}>Konfidenz</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {aiResults.map((change, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ color: "#000" }}>{change.therapyId}</TableCell>
                            <TableCell align="right" sx={{ color: "#000" }}>{formatCurrency(change.oldPrice)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: "primary.main" }}>
                              {formatCurrency(change.newPrice ?? change.price)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`${((change.confidence ?? 0) * 100).toFixed(0)}%`}
                                size="small"
                                color={
                                  (change.confidence ?? 0) > 0.8
                                    ? "success"
                                    : (change.confidence ?? 0) > 0.5
                                    ? "warning"
                                    : "error"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="Übernehmen">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleAcceptAiChange(change)}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Ablehnen">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      setAiResults((prev) => prev.filter((c) => c !== change))
                                    }
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => {
                        aiResults.forEach(handleAcceptAiChange);
                      }}
                    >
                      Alle übernehmen
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setAiResults([])}
                    >
                      Alle ablehnen
                    </Button>
                  </Box>
                </Box>
              )}

              {processAi.isSuccess && aiResults.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Keine Preisänderungen erkannt oder alle bereits übernommen.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
          <TextField
            placeholder="Suchen…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
          <TextField
            select
            label="Heilmittel"
            value={therapyFilter}
            onChange={(e) => setTherapyFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Alle</MenuItem>
            {therapies.map((t: any) => (
              <MenuItem key={t.id} value={t.id}>
                {t.abbreviation} – {t.description}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate} size="small">
          Neuer Preis
        </Button>
      </Box>

      {/* Price Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Heilmittel</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Beschreibung</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Position</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="right">Preis</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Gültig ab</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Gültig bis</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {search || therapyFilter ? "Keine Treffer" : "Keine Preise vorhanden"}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p: PriceRange) => {
                const now = new Date();
                const validFrom = p.validFrom ? new Date(p.validFrom) : null;
                const invalidFrom = p.invalidFrom ? new Date(p.invalidFrom) : null;
                const isActive =
                  validFrom && validFrom <= now && (!invalidFrom || invalidFrom > now);

                return (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleEdit(p)}
                  >
                    <TableCell>
                      <Chip label={p.therapy?.id} size="small" sx={{ fontWeight: 600, color: "#000", borderColor: "#333" }} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ color: "#000" }}>{p.therapy?.abbreviation || "–"}</TableCell>
                    <TableCell align="center" sx={{ color: "#000", fontFamily: "monospace", fontSize: "0.85rem" }}>{p.lineItem ?? "–"}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#000" }}>
                      {formatCurrency(p.price)}
                    </TableCell>
                    <TableCell sx={{ color: "#000" }}>{formatDate(p.validFrom)}</TableCell>
                    <TableCell sx={{ color: "#000" }}>{formatDate(p.invalidFrom)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={isActive ? "Aktiv" : "Inaktiv"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant={isActive ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => handleEdit(p)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => handleDelete(p)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        {filtered.length} von {prices.length} Preise
      </Typography>

      {/* Modal */}
      <ModalPrice open={modalOpen} onClose={() => setModalOpen(false)} editItem={editItem} />
    </Box>
  );
};

export default PricesTab;
